import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { emberApi, type TreeNode } from '../api';
import { BanyanScene, pickArrivalSpot, type Arrival } from '../tree/BanyanScene';
import { track } from '../../../../../shared-web/track';
import './EmberArrival.css';

/** The ceremony's tempo against the Result page's (§4.17): about four seconds
    in all (2026-09-15 拍板; the 0.4 of 09-12 ran it in 2.2 s and none of its
    beats had time to register). */
const TEMPO = 0.6;
/** The night settles before anyone speaks — the layer's 220 ms fade and a beat. */
const HOLD_MS = 400;
/** Speaking, then a breath with the light resting in the bubble: the Result
    page's 2200 ms at tempo, after the 200 ms the scene's speaking animations
    wait before they start (BanyanScene.css). */
const SPEAK_MS = 200 + 2200 * TEMPO;
/** The spark's flight (the SMIL dur in BanyanScene, at tempo). */
const FLIGHT_MS = 1500 * TEMPO;
/** The new light stands a beat before the page leaves it. */
const LIT_MS = 350;
/** The page's flight down to the thread (matches the CSS transition). */
const PAPER_MS = 650;
const PAPER_W = 34;
const PAPER_H = 44;

/**
 * An Ember arriving in the conversation (2026-09-12 拍板, §4.5 / §4.17): the
 * first screen again, translucent, over the thread — the teller's back under
 * the tree, talking; the light gathering at their mouth climbs into the
 * canopy and stands lit; then a small page leaves the new light and flies
 * down to where the Ember sits in the thread, and lands as its cover card.
 * About four seconds. Nothing here takes a pointer: the person keeps typing.
 *
 * `emberIds` are the Embers this one ceremony brings in (several at once
 * since 2026-09-15); the page flies to the last one's card (`[data-ember-id]`,
 * drawn as an invisible slot by the thread); `onLanded` fires as it lands —
 * the thread shows the cards, the Embers are stamped presented. The
 * ceremony's two marks (talk_ember_arrival start / done) are recorded once
 * per Ember, so each Ember's arrival can be read on its own.
 *
 * The thread is put at the card before the first frame, in one step: the
 * flight's end is measured from the card's place on screen, so the card must
 * already be there, and a scroll animating under the translucent scene is
 * the mess this replaced (2026-09-15 dev recording: the thread's smooth
 * scroll ran through the whole ceremony and the page flew to a card 27 000 px
 * below the screen).
 */
export function EmberArrival({ emberIds, onLanded }: { emberIds: readonly string[]; onLanded: () => void }): ReactElement | null {
  const cardId = emberIds[emberIds.length - 1];
  const ids = useRef(emberIds);
  ids.current = emberIds;
  const mark = (state: 'start' | 'done'): void => {
    for (const id of ids.current) track('ui.seen', { name: 'talk_ember_arrival', state, ember_id: id });
  };
  const [animal, setAnimal] = useState<string | null>(null);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [stage, setStage] = useState<Arrival['stage']>('hold');
  const [on, setOn] = useState(false);
  const [still] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [spot] = useState(() => pickArrivalSpot([], window.innerWidth >= 800));
  const arrival = useMemo<Arrival>(() => ({ ...spot, stage, tempo: TEMPO }), [spot, stage]);
  const sceneRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const landed = useRef(onLanded);
  landed.current = onLanded;

  useEffect(() => {
    let alive = true;
    emberApi.treeYou().then((r) => { if (alive) setAnimal(r.animal); }).catch(() => {});
    emberApi.treeView({ size: window.innerWidth < 560 ? 26 : 40 }).then((v) => { if (alive) setNodes(v.nodes); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // One clock from mount; the thread re-rendering under it must not restart it.
  useEffect(() => {
    const card = document.querySelector<HTMLElement>(`[data-ember-id="${cardId}"]`);
    // Reduced motion: the card simply appears where it belongs.
    if (still) {
      card?.scrollIntoView({ block: 'end' });
      landed.current();
      return undefined;
    }
    mark('start');
    card?.scrollIntoView({ block: 'end', behavior: 'instant' });
    const litAt = HOLD_MS + SPEAK_MS + FLIGHT_MS;
    const timers = [
      window.setTimeout(() => setOn(true), 20),
      window.setTimeout(() => setStage('speaking'), HOLD_MS),
      window.setTimeout(() => setStage('flying'), HOLD_MS + SPEAK_MS),
      window.setTimeout(() => setStage('lit'), litAt),
      window.setTimeout(() => {
        // The page leaves the new light for the card's place: both measured
        // now, in viewport pixels, so the flight ends exactly on the card
        // wherever the thread has scrolled to.
        const light = sceneRef.current?.querySelector('.banyan-arrival-light')?.getBoundingClientRect();
        const target = document.querySelector<HTMLElement>(`[data-ember-id="${cardId}"]`)?.getBoundingClientRect();
        const paper = paperRef.current;
        if (!paper || !light || !target) { landed.current(); return; }
        const sx = light.left + light.width / 2 - PAPER_W / 2;
        const sy = light.top + light.height / 2 - PAPER_H / 2;
        const ex = target.left + target.width / 2 - PAPER_W / 2;
        const ey = target.top + target.height / 2 - PAPER_H / 2;
        paper.style.transform = `translate(${sx}px, ${sy}px) scale(0.4) rotate(-12deg)`;
        paper.style.opacity = '1';
        // The start has to be computed before the end is set, or the
        // transition runs from the paper's resting transform — the top-left
        // corner of the screen, at full size (2026-09-15 recording).
        void paper.getBoundingClientRect();
        window.requestAnimationFrame(() => {
          paper.classList.add('is-flying');
          const scaleX = Math.min(target.width / PAPER_W, 8);
          const scaleY = Math.min(target.height / PAPER_H, 8);
          paper.style.transform = `translate(${ex}px, ${ey}px) scale(${scaleX.toFixed(2)}, ${scaleY.toFixed(2)}) rotate(0deg)`;
        });
      }, litAt + LIT_MS),
      window.setTimeout(() => {
        if (paperRef.current) paperRef.current.style.opacity = '0';
        setOn(false);
        mark('done');
        landed.current();
      }, litAt + LIT_MS + PAPER_MS),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [cardId, still]);

  if (still) return null;
  return (
    <div className={`ember-arrival${on ? ' is-on' : ''}`} aria-hidden>
      <div className="ember-arrival-wash" />
      <div ref={sceneRef} className="ember-arrival-scene">
        <BanyanScene nodes={nodes} animal={animal} arrival={arrival} pannable={false} wide={window.innerWidth >= 800} />
      </div>
      <div ref={paperRef} className="ember-arrival-paper" />
    </div>
  );
}
